import { at, reduce, sortBy } from 'lodash-es';
import { createSelector } from 'reselect';

import type { SearchItemEntity } from '@iconik-io/shared-lib/src/redux/entities/normalized/search_item';
import type { Entities } from '@iconik-io/shared-lib/src/redux/entities/types';

import type { AssetRelationsAppState } from '../../ducks/asset_relations/types';
import type { AppState } from '../../store/types';

export type GroupedRelationsItem = { key: string; value: SearchItemEntity[] };

const getAssetId = (_state: AppState, assetId: string | undefined) => assetId;

const getRelationIds = (state: AppState, assetId: string | undefined) =>
  assetId ? state.assetRelations.relations[assetId] : undefined;

const getRelationEntities = (state: AppState) => state.entities.asset_relations;

const EMPTY_DELETED_RELATIONS: AssetRelationsAppState['deletedRelations'][string] =
  [];

export const selectDeletedRelations = (
  state: AppState,
  assetId: string | undefined,
) =>
  assetId
    ? (state.assetRelations.deletedRelations[assetId] ??
      EMPTY_DELETED_RELATIONS)
    : EMPTY_DELETED_RELATIONS;

export const selectRelationsLabels = (state: AppState) =>
  state.assetRelations.relationsLabels;

/**
 * Relations of the given asset, grouped by relation type and sorted by it.
 */
export const selectGroupedRelations = createSelector(
  [getRelationIds, getRelationEntities, getAssetId],
  (relationIds, relationEntities, assetId): GroupedRelationsItem[] => {
    let relations: SearchItemEntity[] = [];
    if (relationIds) {
      const entities: Entities<SearchItemEntity> = relationEntities;
      relations = at(entities, relationIds).filter(
        (item) => item !== undefined,
      );
    }

    const relationsByType = reduce<
      (typeof relations)[number],
      Record<string, SearchItemEntity[]>
    >(
      relations,
      (result, value) => {
        const reduced = result;
        if (!value.relations) {
          return reduced;
        }
        value.relations.forEach((item) => {
          const relationType = item.relation_type;

          const includesAsset = [
            item.related_to_asset_id,
            item.related_from_asset_id,
          ].includes(assetId);

          if (includesAsset && relationType) {
            if (!reduced[relationType]) {
              reduced[relationType] = [];
            }
            reduced[relationType].push(value);
          }
        });
        return reduced;
      },
      {},
    );

    return sortBy(
      reduce<Record<string, SearchItemEntity[]>, GroupedRelationsItem[]>(
        relationsByType,
        (result, value, key) => {
          result.push({ key, value });
          return result;
        },
        [],
      ),
      'key',
    );
  },
);
